/**
 * ConfirmationModal - GitHub-style confirmation dialog
 */
import { XMarkIcon } from '@heroicons/react/24/outline';
import React from 'react';

export type ModalVariant = 'approve' | 'reject' | 'request-changes' | 'danger';

interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (comment?: string) => void;
  title: string;
  message: string;
  confirmText: string;
  cancelText?: string;
  variant?: ModalVariant;
  isLoading?: boolean;
  showCommentInput?: boolean;
  commentPlaceholder?: string;
  isCommentRequired?: boolean;
  commentValidationMessage?: string;
}

export const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText,
  cancelText = 'Cancel',
  variant = 'approve',
  isLoading = false,
  showCommentInput = false,
  commentPlaceholder = 'Enter your comment here...',
  isCommentRequired = false,
  commentValidationMessage = 'A comment is required.',
}) => {
  const [comment, setComment] = React.useState('');
  const commentMissing = isCommentRequired && !comment.trim();

  React.useEffect(() => {
    if (!isOpen) {
      setComment('');
    }
  }, [isOpen]);

  if (!isOpen) {return null;}

  const handleConfirm = () => {
    if (commentMissing) {
      return;
    }

    onConfirm(comment);
    setComment(''); // Reset after confirm
  };

  const handleClose = () => {
    setComment('');
    onClose();
  };

  const getVariantStyles = () => {
    switch (variant) {
      case 'approve':
        return {
          button: 'bg-[#008557] hover:bg-[#006b45] text-white',
          icon: '✓',
        };
      case 'reject':
      case 'danger':
        return {
          button: 'bg-red-600 hover:bg-red-700 text-white',
          icon: '✕',
        };
      case 'request-changes':
        return {
          button: 'bg-yellow-600 hover:bg-yellow-700 text-white',
          icon: '⚠',
        };
    }
  };

  const styles = getVariantStyles();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <button
        type="button"
        tabIndex={-1}
        className="absolute inset-0 bg-black/50 backdrop-blur-sm cursor-default border-0 p-0"
        onClick={handleClose}
        aria-label="Close dialog"
      />

      {/* Modal */}
      <div className="relative z-10 w-full max-w-md mx-4 bg-white rounded-lg shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <button
            onClick={handleClose}
            className="p-1 rounded-md hover:bg-gray-100 transition-colors"
            disabled={isLoading}
          >
            <XMarkIcon className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6">
          <div className="flex gap-4">
            <div
              className={`flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-full text-xl ${
                variant === 'approve'
                  ? 'bg-green-100'
                  : variant === 'reject' || variant === 'danger'
                    ? 'bg-red-100'
                    : 'bg-yellow-100'
              }`}
            >
              {styles.icon}
            </div>
            <p className="text-sm text-gray-700 leading-relaxed">{message}</p>
          </div>
          {showCommentInput && (
            <div className="mt-4 ml-14">
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder={commentPlaceholder}
                className="w-full h-24 p-3 border border-gray-300 rounded-md focus:ring-[#0078d4] focus:border-[#0078d4] text-sm resize-none"
                disabled={isLoading}
              />
              {isCommentRequired && (
                <p className="mt-2 text-xs font-medium text-gray-600">
                  {commentValidationMessage}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-4 border-t border-gray-200 bg-gray-50">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-md transition-colors disabled:opacity-50"
            disabled={isLoading}
          >
            {cancelText}
          </button>
          <button
            onClick={handleConfirm}
            className={
              'px-4 py-2 text-sm font-medium rounded-md transition-colors ' +
              `disabled:opacity-50 ${styles.button}`
            }
            disabled={isLoading || commentMissing}
          >
            {isLoading ? (
              <span className="flex items-center gap-2">
                <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Processing...
              </span>
            ) : (
              confirmText
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
